import { useMemo } from 'react';
import get from 'lodash/get';

import useViewport from 'Hooks/useViewport';

// These basic settings will be used if none are provided.
const DEFAULT_SETTINGS = {
  xs: {
    slidesToShow: 1,
    slidesToScroll: 1,
    sliderWidth: 375,
    dots: true,
    arrows: false,
    sliderGap: 16,
  },
  sm: {
    slidesToShow: 2,
    slidesToScroll: 2,
    sliderWidth: 524,
    dots: true,
    arrows: false,
    sliderGap: 16,
  },
  md: {
    slidesToShow: 2,
    slidesToScroll: 2,
    sliderWidth: 704,
    dots: true,
    arrows: false,
    sliderGap: 16,
  },
  lg: {
    slidesToShow: 3,
    slidesToScroll: 3,
    sliderWidth: 944,
    dots: true,
    arrows: true,
    sliderGap: 24,
  },
  xl: {
    slidesToShow: 4,
    slidesToScroll: 4,
    sliderWidth: 1124,
    dots: true,
    arrows: true,
    sliderGap: 24,
  },
  xxl: {
    slidesToShow: 4,
    slidesToScroll: 4,
    sliderWidth: 1324,
    dots: true,
    arrows: true,
    sliderGap: 32,
  },
};

/**
 * Custom hook to calculate carousel settings based on viewport
 * @param {Object} sliderSettings - Custom slider settings
 * @param {number} totalSlides - Total number of slides in the carousel
 * @returns {Object} Calculated carousel settings
 */

export default function useSliderSettings(sliderSettings = {}, totalSlides) {
  const { currentBreakpoint, isBelowLg } = useViewport();

  return useMemo(() => {
    const breakpointDefault = DEFAULT_SETTINGS[currentBreakpoint];
    const breakpointCustom = sliderSettings[currentBreakpoint] || {};

    const settings = {
      slidesToShow: get(breakpointCustom, 'slidesToShow', breakpointDefault.slidesToShow),
      slidesToScroll: get(breakpointCustom, 'slidesToScroll', breakpointDefault.slidesToScroll),
      sliderWidth: get(breakpointCustom, 'sliderWidth', breakpointDefault.sliderWidth),
      sliderGap: get(breakpointCustom, 'sliderGap', breakpointDefault.sliderGap),
      showDots: get(breakpointCustom, 'dots', breakpointDefault.dots),
      showArrows: get(breakpointCustom, 'arrows', breakpointDefault.arrows),
    };

    // Hide controls if not needed
    if (totalSlides <= settings.slidesToShow) {
      settings.showDots = false;
      settings.showArrows = false;
    }

    // Remove dots on touch devices
    if (isBelowLg) {
      settings.showDots = false;
    }

    return settings;
  }, [sliderSettings, currentBreakpoint, totalSlides, isBelowLg]);
}
